// ViewModeShell — fixed TV presentation canvas that always matches the REAL
// visible viewport (iPad Safari toolbars included): height comes from
// window.visualViewport when available, else 100dvh, else 100vh.
// Compact fixed-height header; body fills exactly the remainder (min-h-0,
// overflow hidden) — no browser or internal scrolling.
import React, { useEffect, useState } from 'react';
import rcMark from '../../assets/rc-mark.png';

export const VIEW_MODE_HEADER_H = 64;

function useVisibleHeight() {
  const read = () => (typeof window !== 'undefined'
    ? Math.round(window.visualViewport?.height || window.innerHeight)
    : 0);
  const [h, setH] = useState(read);
  useEffect(() => {
    const update = () => setH(read());
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    // iPad Safari reports a transient viewport on first navigation/entry
    // (toolbars still settling), which is why the shell looked correct only
    // after a manual refresh. Re-measure across a few post-mount frames so
    // the FIRST load converges to the same state a refresh produces.
    window.addEventListener('pageshow', update);
    update();
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(update));
    const t1 = setTimeout(update, 120);
    const t2 = setTimeout(update, 400);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.removeEventListener('pageshow', update);
      cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);
  return h;
}

export default function ViewModeShell({ eventName, viewLabel, viewOptions, activeView, onViewChange, lastUpdated, children }) {
  const visibleH = useVisibleHeight();
  const timeStr = lastUpdated
    ? lastUpdated.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })
    : '—';

  useEffect(() => {
    const html = document.documentElement; const body = document.body;
    const prev = [html.style.overflow, body.style.overflow, html.style.height, body.style.height, body.style.position];
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden';
    html.style.height = '100%'; body.style.height = '100%';
    body.style.position = 'fixed';
    body.style.inset = '0';
    // Pin the fixed presentation canvas to the top of the visible viewport.
    // On first iPad-Safari entry the page can be left scrolled from the prior
    // route, which pushed the header above the visible area until a refresh.
    window.scrollTo(0, 0);
    return () => {
      [html.style.overflow, body.style.overflow, html.style.height, body.style.height, body.style.position] = prev;
      body.style.inset = '';
    };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [visibleH]);

  return (
    <div
      className="fixed top-0 left-0 w-full bg-white text-[#013523] font-sans overflow-hidden flex flex-col"
      data-testid="view-mode-root"
      data-visible-h={visibleH}
      style={{
        height: visibleH ? `${visibleH}px` : '100dvh',
        minHeight: 0,
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        letterSpacing: '0.005em',
      }}
    >
      <header
        data-testid="view-mode-header"
        className="shrink-0 w-full border-b border-[#e3dcd1] flex items-center gap-5 px-5"
        style={{ height: VIEW_MODE_HEADER_H }}
      >
        <div className="flex items-center gap-3.5 min-w-0 flex-1">
          <img src={rcMark} alt="Rippers Club" className="shrink-0 h-10 w-auto" data-testid="view-mode-mark" />
          <div className="min-w-0 flex items-baseline gap-3">
            <div className="font-serif text-[#013523] leading-none truncate text-[22px]" data-testid="view-mode-event-name">
              {eventName || '—'}
            </div>
            <div className="hidden md:block text-[#c9a93a] font-semibold uppercase tracking-[0.22em] leading-none text-[10px]" data-testid="view-mode-club-name">
              The Rippers Club
            </div>
            <div className="text-[#6a766e] uppercase tracking-[0.18em] leading-none text-[10px] shrink-0" data-testid="view-mode-view-label">
              · {viewLabel}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-[#9aa39d] tabular-nums text-[10px] hidden sm:block" data-testid="view-mode-last-updated">
            Last updated {timeStr}
          </div>
          <div className="inline-flex rounded-full border border-[#e3dcd1] bg-[#fbfaf8] p-0.5" data-testid="view-mode-switcher">
            {viewOptions.map((opt) => {
              const active = opt.key === activeView;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onViewChange(opt.key)}
                  data-testid={`view-mode-switcher-${opt.key}`}
                  className={`px-3.5 py-1 rounded-full transition-colors font-semibold tracking-wide text-[12px] ${active ? 'bg-[#013523] text-white' : 'text-[#013523] hover:bg-white'}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main
        className="flex-1 min-h-0 w-full overflow-hidden"
        style={{ padding: '10px 20px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
        data-testid="view-mode-body"
      >
        {children}
      </main>
    </div>
  );
}
